/** Сводка по фактическому оформлению текста абзаца — для правил и превью. */

import { runFonts, type DocModel, type Paragraph, type RunInfo } from '../model/document';

export interface ParaFormat {
  /** Количество букв с учётом только видимого текста. */
  letters: number;
  boldShare: number;
  italicShare: number;
  underlineShare: number;
  capsShare: number;
  /** Кегль (полупункты) → число букв. */
  sizes: Map<number, number>;
  /** Гарнитура → число букв. */
  fonts: Map<string, number>;
  /** Цвет → число букв (только не чёрные и не «авто»). */
  colors: Map<string, number>;
  highlighted: number;
  dominantSize?: number;
  dominantFont?: string;
}

const cache = new WeakMap<Paragraph, ParaFormat>();

const isBlack = (c: string | undefined): boolean => !c || c === 'auto' || c === '000000';

function inc<K>(m: Map<K, number>, k: K, n: number): void {
  m.set(k, (m.get(k) ?? 0) + n);
}

function top<K>(m: Map<K, number>): K | undefined {
  let best: K | undefined;
  let bestN = -1;
  for (const [k, n] of m) if (n > bestN) [best, bestN] = [k, n];
  return best;
}

export function letterCount(s: string): number {
  let n = 0;
  for (const ch of s) if (/\p{L}|\d/u.test(ch)) n++;
  return n;
}

export function paraFormat(model: DocModel, p: Paragraph): ParaFormat {
  const cached = cache.get(p);
  if (cached) return cached;
  const f: ParaFormat = {
    letters: 0,
    boldShare: 0,
    italicShare: 0,
    underlineShare: 0,
    capsShare: 0,
    sizes: new Map(),
    fonts: new Map(),
    colors: new Map(),
    highlighted: 0,
  };
  let bold = 0;
  let italic = 0;
  let underline = 0;
  let caps = 0;
  for (const r of p.runs) {
    const text = p.text.slice(r.start, r.end);
    const n = letterCount(text);
    if (n === 0) continue;
    f.letters += n;
    if (r.props.b) bold += n;
    if (r.props.i) italic += n;
    if (r.props.u && r.props.u !== 'none') underline += n;
    if (r.props.caps) caps += n;
    inc(f.sizes, r.props.sz ?? model.styles.defaultsR.sz ?? 20, n);
    for (const font of runFonts(model, r, text)) inc(f.fonts, font, n);
    if (!isBlack(r.props.color)) inc(f.colors, r.props.color!, n);
    if ((r.props.highlight && r.props.highlight !== 'none') || (r.props.shading && r.props.shading !== 'auto' && r.props.shading !== 'FFFFFF'))
      f.highlighted += n;
  }
  if (f.letters > 0) {
    f.boldShare = bold / f.letters;
    f.italicShare = italic / f.letters;
    f.underlineShare = underline / f.letters;
    f.capsShare = caps / f.letters;
  }
  f.dominantSize = top(f.sizes);
  f.dominantFont = top(f.fonts);
  cache.set(p, f);
  return f;
}

/** Фрагменты с видимыми буквами — только их имеет смысл проверять и исправлять. */
export function textRuns(p: Paragraph): RunInfo[] {
  return p.runs.filter((r) => letterCount(p.text.slice(r.start, r.end)) > 0);
}
